// Defaults (§5): every number 0, identity curves, full-frame crop, background off, transparent fill.
import { HUE_BANDS } from '../color/hsl';
import type { Settings, SyncGroup } from './types';

const identity = () => [
  { x: 0, y: 0 },
  { x: 1, y: 1 },
];

export function defaultEffects(): Settings['effects'] {
  return {
    vignette: { amount: 0, midpoint: 50, feather: 50 },
    grain: { amount: 0, size: 25 },
    splitTone: { shadowHue: 220, shadowSat: 0, highlightHue: 40, highlightSat: 0, balance: 0 },
  };
}

export function defaultUpscale(): Settings['upscale'] {
  return { factor: 1, model: 'general', strength: 50 };
}

export function defaultSettings(): Settings {
  return {
    v: 3,
    light: { exposure: 0, contrast: 0, highlights: 0, shadows: 0, whites: 0, blacks: 0 },
    wb: { temp: 0, tint: 0 },
    color: { vibrance: 0, saturation: 0 },
    hsl: Object.fromEntries(HUE_BANDS.map((b) => [b, { h: 0, s: 0, l: 0 }])) as Settings['hsl'],
    curve: { master: identity(), r: identity(), g: identity(), b: identity() },
    detail: { sharpAmount: 0, sharpRadius: 1, sharpMasking: 0, nrLuma: 0, nrColor: 0 },
    geometry: {
      crop: { x: 0, y: 0, w: 1, h: 1 },
      aspect: null,
      angle: 0,
      vertical: 0,
      horizontal: 0,
      constrain: true,
    },
    masks: [],
    heal: [],
    skin: { amount: 0, strokes: [] },
    cleanWhites: { amount: 0, strokes: null },
    effects: defaultEffects(),
    lut: { id: null, intensity: 100 },
    background: {
      mode: 'off',
      refine: [],
      edge: { feather: 0, shift: 0, decontaminate: 0 },
      fill: { kind: 'transparent', color: [1, 1, 1] },
      shadow: { enabled: false, opacity: 40, blur: 30, offsetY: 2 },
    },
    upscale: defaultUpscale(),
  };
}

/** Default sync selection excludes the per-photo groups (§5). */
export const DEFAULT_SYNC_GROUPS: SyncGroup[] = [
  'light',
  'wb',
  'color',
  'hsl',
  'curve',
  'detail',
  'perspective',
  'cleanWhites',
  'lut',
  'effects',
  'background',
];

export const LIMITS = { masks: 8, heal: 200, history: 200 } as const;
